import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar, Clock, MapPin, DollarSign, Users, Info, Utensils, Car, Phone, Image, PartyPopper, Accessibility } from "lucide-react";
import { Event } from "@/types";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface EventManagerProps {
  onCancel?: () => void;
}

export const EventManager = ({ onCancel }: EventManagerProps) => {
  const [eventData, setEventData] = useState<Partial<Event>>({
    name: "",
    description: "",
    ticket_price: 0,
    capacity: 100,
    status: "active",
    total_tickets_sold: 0,
    revenue: 0,
    clicks: 0,
    views: 0,
    image_url: null,
    promoter_id: null,
    created_by: null,
    updated_by: null,
    address: "",
    dress_code: "",
    food_beverages: "",
    parking_info: "",
    public_transport: "",
    accessibility_info: "",
    age_restriction: "",
    contact_email: "",
    contact_phone: "",
    social_media_links: {},
    featured_guests: "",
    event_schedule: "",
    safety_guidelines: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!eventData.name || !eventData.start_time) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const startTime = new Date(eventData.start_time);
      const endTime = eventData.end_time 
        ? new Date(eventData.end_time) 
        : new Date(startTime.getTime() + (6 * 60 * 60 * 1000)); // 6 hours default if not specified

      const newEvent = {
        name: eventData.name,
        description: eventData.description,
        venue_id: "DEFAULT_VENUE_ID", // This should be replaced with actual venue ID
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        ticket_price: eventData.ticket_price || 0,
        capacity: eventData.capacity || 100,
        status: eventData.status || "active",
        image_url: eventData.image_url,
        clicks: 0,
        views: 0,
        revenue: 0,
        total_tickets_sold: 0,
        promoter_id: null,
        event_details: JSON.stringify({
          address: eventData.address,
          dress_code: eventData.dress_code,
          food_beverages: eventData.food_beverages,
          parking_info: eventData.parking_info,
          public_transport: eventData.public_transport,
          accessibility_info: eventData.accessibility_info,
          age_restriction: eventData.age_restriction,
          contact_email: eventData.contact_email,
          contact_phone: eventData.contact_phone,
          social_media_links: eventData.social_media_links,
          featured_guests: eventData.featured_guests,
          event_schedule: eventData.event_schedule,
          safety_guidelines: eventData.safety_guidelines
        })
      };

      const { error } = await supabase
        .from("events")
        .insert([newEvent]);

      if (error) throw error;
      
      toast.success("Event created successfully!");
      onCancel?.();
    } catch (error) {
      toast.error("Failed to create event");
      console.error("Error creating event:", error);
    }
  };

  return (
    <Card className="p-6 glass-card">
      <form onSubmit={handleSubmit} className="space-y-6">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="mb-4 w-full max-w-md mx-auto grid grid-cols-4">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="logistics">Logistics</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Event Name*</Label>
              <Input
                id="name"
                value={eventData.name}
                onChange={(e) => setEventData({ ...eventData, name: e.target.value })}
                placeholder="Summer Night Party"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time">Start Date & Time*</Label>
                <div className="relative">
                  <Input
                    id="start_time"
                    type="datetime-local"
                    value={eventData.start_time}
                    onChange={(e) => setEventData({ ...eventData, start_time: e.target.value })}
                    required
                  />
                  <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_time">End Date & Time</Label>
                <div className="relative">
                  <Input
                    id="end_time"
                    type="datetime-local"
                    value={eventData.end_time}
                    onChange={(e) => setEventData({ ...eventData, end_time: e.target.value })}
                  />
                  <Clock className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ticket_price">Ticket Price ($)*</Label>
                <div className="relative">
                  <Input
                    id="ticket_price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={eventData.ticket_price}
                    onChange={(e) => setEventData({ ...eventData, ticket_price: parseFloat(e.target.value) })}
                    required
                  />
                  <DollarSign className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity*</Label>
                <div className="relative">
                  <Input
                    id="capacity"
                    type="number"
                    min="1"
                    value={eventData.capacity}
                    onChange={(e) => setEventData({ ...eventData, capacity: parseInt(e.target.value) })}
                    required
                  />
                  <Users className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground" />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_url">Event Image URL</Label>
              <div className="relative">
                <Input
                  id="image_url"
                  type="text"
                  value={eventData.image_url || ""}
                  onChange={(e) => setEventData({ ...eventData, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
                <Image className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Event Description*</Label>
              <Textarea
                id="description"
                value={eventData.description || ""}
                onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                placeholder="Tell potential guests about your event..."
                rows={4}
                required
              />
            </div>
          </TabsContent>

          <TabsContent value="details" className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="featured_guests">Featured Guests/Performers</Label>
              <Textarea
                id="featured_guests"
                value={eventData.featured_guests || ""}
                onChange={(e) => setEventData({ ...eventData, featured_guests: e.target.value })}
                placeholder="List featured guests, speakers or performers..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="event_schedule">Event Schedule/Agenda</Label>
              <Textarea
                id="event_schedule"
                value={eventData.event_schedule || ""}
                onChange={(e) => setEventData({ ...eventData, event_schedule: e.target.value })}
                placeholder="Provide a detailed schedule of activities..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dress_code">Dress Code</Label>
                <Input
                  id="dress_code"
                  value={eventData.dress_code || ""}
                  onChange={(e) => setEventData({ ...eventData, dress_code: e.target.value })}
                  placeholder="Casual, Formal, etc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="age_restriction">Age Restriction</Label>
                <Select 
                  onValueChange={(value) => setEventData({ ...eventData, age_restriction: value })}
                  value={eventData.age_restriction || ""}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select age restriction" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_ages">All Ages</SelectItem>
                    <SelectItem value="18_plus">18+</SelectItem>
                    <SelectItem value="21_plus">21+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="safety_guidelines">Safety & Security Guidelines</Label>
              <Textarea
                id="safety_guidelines"
                value={eventData.safety_guidelines || ""}
                onChange={(e) => setEventData({ ...eventData, safety_guidelines: e.target.value })}
                placeholder="Describe safety procedures and guidelines..."
                rows={3}
              />
            </div>
          </TabsContent>

          <TabsContent value="logistics" className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="address">Venue Address*</Label>
              <div className="relative">
                <Input
                  id="address"
                  value={eventData.address || ""}
                  onChange={(e) => setEventData({ ...eventData, address: e.target.value })}
                  placeholder="123 Main St, City, State, Zip"
                  required
                />
                <MapPin className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="parking_info">Parking Information</Label>
                <Input
                  id="parking_info"
                  value={eventData.parking_info || ""}
                  onChange={(e) => setEventData({ ...eventData, parking_info: e.target.value })}
                  placeholder="Parking details and options..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="public_transport">Public Transport</Label>
                <Input
                  id="public_transport"
                  value={eventData.public_transport || ""}
                  onChange={(e) => setEventData({ ...eventData, public_transport: e.target.value })}
                  placeholder="Nearby public transportation options..."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="food_beverages">Food & Beverages</Label>
                <Input
                  id="food_beverages"
                  value={eventData.food_beverages || ""}
                  onChange={(e) => setEventData({ ...eventData, food_beverages: e.target.value })}
                  placeholder="Available food and drink options..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="accessibility_info">Accessibility Information</Label>
                <Input
                  id="accessibility_info"
                  value={eventData.accessibility_info || ""}
                  onChange={(e) => setEventData({ ...eventData, accessibility_info: e.target.value })}
                  placeholder="Wheelchair access, etc."
                />
                <Accessibility className="absolute right-3 top-2.5 h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contact" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={eventData.contact_email || ""}
                  onChange={(e) => setEventData({ ...eventData, contact_email: e.target.value })}
                  placeholder="contact@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input
                  id="contact_phone"
                  value={eventData.contact_phone || ""}
                  onChange={(e) => setEventData({ ...eventData, contact_phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Social Media Links</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  placeholder="Instagram URL"
                  value={(eventData.social_media_links as any)?.instagram || ""}
                  onChange={(e) => setEventData({ 
                    ...eventData, 
                    social_media_links: {
                      ...(eventData.social_media_links || {}),
                      instagram: e.target.value
                    }
                  })}
                />
                <Input
                  placeholder="Facebook URL"
                  value={(eventData.social_media_links as any)?.facebook || ""}
                  onChange={(e) => setEventData({ 
                    ...eventData, 
                    social_media_links: {
                      ...(eventData.social_media_links || {}),
                      facebook: e.target.value
                    }
                  })}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-4 pt-4 border-t">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit">
            <PartyPopper className="mr-2 h-4 w-4" />
            Create Event
          </Button>
        </div>
      </form>
    </Card>
  );
};
